import { withStaticProperties } from '@tamagui/core';

import { BottomNav } from './BottomNav';
import { FormContent } from './FormContent';
import { FormLayoutProvider } from './FormLayoutProvider';
import { LegalCopy } from './LegalCopy';
import { QuestionContainer } from './QuestionContainer';
import { ScrollContainer } from './ScrollContainer';
import { Title } from './Title';
import { TopNav } from './TopNav';

/**
 * `FormLayout` is a suite of composable components for building a full-page
 * form. Each subcomponent has its own purpose and can be swapped out with a
 * custom component as needed. Typical usage looks something like this:
 * ```
 * <FormLayout onSubmit={handleSubmit}>
 *   <FormLayout.ScrollContainer>
 *     <FormLayout.TopNav {...topNavProps} />
 *     <FormLayout.FormContent {...formContentProps}>
 *       <FormLayout.Title {...titleProps} />
 *       <FormLayout.QuestionContainer>
 *         {questionComponents}
 *       </FormLayout.QuestionContainer>
 *     </FormLayout.FormContent>
 *   </FormLayout.ScrollContainer>
 *   <FormLayout.BottomNav {...bottomNavProps} />
 * </FormLayout>
 * ```
 * Refer to the docs for each subcomponent for more details on how to use them.
 *
 * If you only have a single input in your form, you can pass the id of the
 * input element as the `<FormLayout>`'s `singleQuestionInputId` prop. This will
 * let you pass an empty string as the title prop for the input (which would
 * normally throw an error) and use the `<FormLayout.Title>` as the input's
 * label (to maintain a11y). Make sure the id passed to the input component
 * and the id passed as the `singleQuestionInputId` prop match!
 *
 * If you want to include legal copy in your form, you can use
 * `<FormLayout.LegalCopy>`. Depending on where the copy should go in the
 * layout, you can use `<Box>` and BoxProps to position it where you want. For
 * example:
 * ```
 * <FormLayout onSubmit={handleSubmit}>
 *   <FormLayout.ScrollContainer>
 *     <FormLayout.TopNav {...topNavProps} />
 *     <FormLayout.FormContent {...formContentProps} paddingBottom="$44x">
 *       <FormLayout.Title {...titleProps} />
 *       <FormLayout.QuestionContainer>
 *         {questionComponents}
 *       </FormLayout.QuestionContainer>
 *       <Box
 *         position="absolute"
 *         bottom={0}
 *         left={0}
 *         right={0}
 *         alignItems="center"
 *         paddingHorizontal="$12x"
 *       >
 *         <FormLayout.LegalCopy {...legalCopyProps} />
 *       </Box>
 *     </FormLayout.FormContent>
 *   </FormLayout.ScrollContainer>
 *   <FormLayout.BottomNav {...bottomNavProps} />
 * </FormLayout>
 * ```
 *
 * Note that this component doesn't change the props of the input components
 * that you pass as children. You will probably want to use the `useMedia` hook
 * or media props like `$largerThanSM`, etc., to adjust the styling of the
 * inputs at different breakpoints.
 */
export const FormLayout = withStaticProperties(FormLayoutProvider, {
  BottomNav,
  FormContent,
  LegalCopy,
  QuestionContainer,
  ScrollContainer,
  Title,
  TopNav,
});

export type { TopNavProps as FormLayoutTopNavProps } from './TopNav';
export type { TitleProps as FormLayoutTitleProps } from './Title';
export type { BottomNavProps as FormLayoutBottomNavProps } from './BottomNav';
